import { useState, useEffect, useMemo, useCallback } from 'react';
import { Plus, BookOpen, Award, ClipboardCheck, BarChart3, GraduationCap } from 'lucide-react';
import ModulLayout from '../../layout/ModulLayout';
import ListToolbar, { PohledPill } from '../../layout/ListToolbar';
import KDoreseniRadek, { KDoreseniSeznam } from '../../layout/KDoreseni';
import ModalShell from '../../ui/ModalShell';
import SkoleniEditor from './SkoleniEditor';
import { SECTION_COLOR, datum, prijmeniJmeno, stavCertifikatu } from '../lide';
import { Pole, Vstup, Vyber, Tlacitko, Hlaska, muted, linka } from '../karta/ui';

/**
 * Lidé → Školení (dřív Vzdělávání → Správa školení).
 *
 * `ModulLayout`: bez výběru mřížka přes celou šířku, po výběru zúžený seznam +
 * **editor jako detail** (dřív samostatná stránka, která seznam zavřela) +
 * třetí sloupec se splněním a „K dořešení". Když se edituje obsah sekce, třetí
 * sloupec se schová, ať má editor (TipTap, video) šířku.
 */

const TYP_LABEL = { skoleni: 'Pouze školením', kviz: 'Kvízem', kviz_certifikace: 'Kvíz + certifikace', kviz_kvalifikace: 'Kvíz + kval. matice' };
const TYP_IKONA = { skoleni: BookOpen, kviz: ClipboardCheck, kviz_certifikace: Award, kviz_kvalifikace: BarChart3 };
const STAV_LABEL = { koncept: 'Koncept', aktivni: 'Aktivní', archivovano: 'Archivováno' };

const POHLEDY = [
    { id: 'aktivni', label: 'Aktivní', test: s => s.stav === 'aktivni' },
    { id: 'koncept', label: 'Koncepty', test: s => s.stav === 'koncept' },
    { id: 'termin', label: 'Po termínu', test: s => (s.po_terminu_count || 0) > 0, upozorneni: true },
    { id: 'archiv', label: 'Archiv', test: s => s.stav === 'archivovano' },
];

const dnes = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };

export default function SpravaSkoleni({
    isDark, isMobile, cardStyle, inputStyle, mutedColor,
    zamestnanci = [], certTypy = [], initialSkoleniId = null, rezervaDole = null,
}) {
    const [skoleni, setSkoleni] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hledani, setHledani] = useState('');
    const [pohled, setPohled] = useState('aktivni');
    const [vybranyId, setVybranyId] = useState(initialSkoleniId || null);
    const [edituje, setEdituje] = useState(false);
    const [nove, setNove] = useState(null);

    const nacti = useCallback(() => fetch('/api/skoleni').then(r => r.json())
        .then(d => { setSkoleni(Array.isArray(d) ? d : []); setLoading(false); })
        .catch(() => setLoading(false)), []);
    useEffect(() => { nacti(); }, [nacti]);
    useEffect(() => { if (initialSkoleniId) setVybranyId(initialSkoleniId); }, [initialSkoleniId]);

    const aktivniPohled = POHLEDY.find(p => p.id === pohled) || POHLEDY[0];
    const vyfiltrovana = useMemo(() => {
        const q = hledani.trim().toLowerCase();
        return skoleni.filter(s => aktivniPohled.test(s) && (!q || (s.nazev || '').toLowerCase().includes(q)))
            .sort((a, b) => (a.nazev || '').localeCompare(b.nazev || '', 'cs'));
    }, [skoleni, hledani, aktivniPohled]);

    const vybrane = vybranyId ? skoleni.find(s => s.id === vybranyId) : null;

    // K dořešení u školení — jedna funkce pro panel i mobilní modal
    const kDoreseni = useMemo(() => {
        if (!vybrane) return [];
        const polozky = [];
        const d0 = dnes();
        for (const p of vybrane.prubehy || []) {
            const termin = p.prirazeni?.termin_do ? new Date(p.prirazeni.termin_do) : null;
            if (p.stav !== 'dokonceno' && termin && termin < d0) {
                polozky.push({
                    id: `termin-${p.id}`, problem: true,
                    titulek: `${p.zamestnanec ? prijmeniJmeno(p.zamestnanec) : 'Zaměstnanec'} — po termínu`,
                    popis: `termín ${datum(p.prirazeni.termin_do)} · ${p.stav === 'zahajeno' ? 'rozpracováno' : p.stav === 'neuspesne' ? 'neúspěšné' : 'nezačal(a)'}`,
                });
            }
        }
        const kod = vybrane.certifikace_typ_kod;
        if (kod) {
            const nazev = certTypy.find(t => t.kod === kod)?.nazev || kod;
            const brzy = zamestnanci.filter(z => stavCertifikatu(z, kod).stav === 'brzy');
            if (brzy.length) {
                polozky.push({
                    id: 'brzy', problem: false,
                    titulek: `${nazev} expiruje do 30 dní · ${brzy.length}`,
                    popis: brzy.map(z => prijmeniJmeno(z)).join(', '),
                });
            }
            const chybi = zamestnanci.filter(z => ['chybi', 'expirovano'].includes(stavCertifikatu(z, kod).stav));
            if (chybi.length) {
                polozky.push({
                    id: 'chybi', problem: true,
                    titulek: `${nazev} chybí · ${chybi.length}`,
                    popis: chybi.map(z => prijmeniJmeno(z)).join(', '),
                });
            }
        }
        if (vybrane.stav === 'koncept' && !vybrane.prirazeni_count) {
            polozky.push({ id: 'koncept', problem: false, titulek: 'Koncept bez přiřazení', popis: 'aktivujte a přiřaďte v záložce Přiřazení' });
        }
        return polozky;
    }, [vybrane, zamestnanci, certTypy]);

    const hranice = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)';
    const grid = { display: 'grid', gridTemplateColumns: 'minmax(240px, 2.4fr) 110px minmax(140px, 1.2fr) 70px 90px 180px 100px minmax(120px, 1fr)', columnGap: 14, alignItems: 'center' };

    const seznam = ({ siroky }) => (
        <div className="flex flex-col" style={{ flex: 1, minHeight: 0 }}>
            <ListToolbar siroky={siroky} kompaktni={isMobile} sklo isDark={isDark} mutedColor={mutedColor} inputStyle={inputStyle} barva={SECTION_COLOR}
                hledani={{ value: hledani, onChange: setHledani, placeholder: 'Hledat školení…', placeholderKratky: 'Hledat' }}
                akce={[{ id: 'nove', label: 'Nové školení', Icon: Plus, onClick: () => setNove({ nazev: '', typ_zakonceni: 'skoleni' }), primarni: true }]}
                // Ve zúženém sloupci ovladač místo dvou řad pilulek (norma §2)
                pohledy={siroky || isMobile ? null : POHLEDY.map(p => ({ id: p.id, label: p.label, pocet: skoleni.filter(p.test).length, upozorneni: p.upozorneni, barva: p.upozorneni ? '#EF4444' : undefined }))}
                pohledAktivni={pohled}
                onPohled={setPohled}
                pohled={!(siroky || isMobile) ? null : POHLEDY.map(p => {
                    const n = skoleni.filter(p.test).length;
                    return (
                        <PohledPill key={p.id} label={p.label} pocet={n} aktivni={pohled === p.id} upozorneni={p.upozorneni && n > 0}
                            onClick={() => setPohled(p.id)} barva={SECTION_COLOR} isDark={isDark} mutedColor={mutedColor} kompaktni={isMobile} />
                    );
                })} />

            {siroky && vyfiltrovana.length > 0 && (
                <div style={{ ...grid, padding: '10px 14px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', color: mutedColor, textTransform: 'uppercase', flexShrink: 0 }}>
                    <span>Název</span><span>Stav</span><span>Typ zakončení</span><span style={{ textAlign: 'right' }}>Sekcí</span>
                    <span style={{ textAlign: 'right' }}>Přiřazeno</span><span>Splnění</span><span style={{ textAlign: 'right' }}>Po termínu</span><span>Upraveno</span>
                </div>
            )}

            <div className="overflow-y-auto" style={{ flex: 1, minHeight: 0, borderTop: siroky ? `1px solid ${hranice}` : 'none', paddingBottom: rezervaDole || undefined }}>
                {loading ? (
                    <p className="text-center text-xs py-8" style={{ color: mutedColor }}>Načítám…</p>
                ) : vyfiltrovana.length === 0 ? (
                    <div className="text-center px-6 py-10">
                        <GraduationCap size={26} style={{ color: mutedColor, opacity: 0.5, margin: '0 auto 10px' }} />
                        <p className="text-xs" style={{ color: mutedColor }}>{hledani.trim() ? `Nic nenalezeno pro „${hledani}"` : 'V tomhle pohledu žádné školení není.'}</p>
                    </div>
                ) : vyfiltrovana.map(s => (
                    siroky
                        ? <RadekMrizka key={s.id} s={s} grid={grid} aktivni={vybranyId === s.id} onClick={() => setVybranyId(s.id)} isDark={isDark} mutedColor={mutedColor} hranice={hranice} />
                        : <RadekUzky key={s.id} s={s} aktivni={vybranyId === s.id} onClick={() => setVybranyId(s.id)} kompaktni={isMobile} mutedColor={mutedColor} hranice={hranice} />
                ))}
            </div>
        </div>
    );

    const panel = () => {
        if (!vybrane || edituje) return null;
        const prubehy = vybrane.prubehy || [];
        const pocet = (st) => prubehy.filter(p => p.stav === st).length;
        const celkem = vybrane.prubehy_count || prubehy.length;
        const hotovo = vybrane.dokonceno_count || 0;
        return (
            <div className="rounded-xl" style={{ ...cardStyle, padding: '14px 16px' }}>
                <p className="text-[10.5px] font-bold uppercase" style={{ color: muted(isDark), letterSpacing: '0.06em' }}>Splnění</p>
                <p className="font-bold tabular-nums" style={{ fontSize: 28, color: 'var(--text-primary)', margin: '2px 0 6px' }}>
                    {hotovo} <span style={{ fontSize: 16, color: muted(isDark), fontWeight: 500 }}>/ {celkem}</span>
                </p>
                <div style={{ height: 5, borderRadius: 3, background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', marginBottom: 10 }}>
                    <div style={{ height: '100%', borderRadius: 3, width: `${celkem ? (hotovo / celkem) * 100 : 0}%`, background: SECTION_COLOR }} />
                </div>
                {[['Dokončilo', hotovo], ['Rozpracováno', pocet('zahajeno')], ['Nezačalo', pocet('prirazeno')], ['Neúspěšné', pocet('neuspesne')], ['Po termínu', vybrane.po_terminu_count || 0]].map(([p, h]) => (
                    <div key={p} className="flex items-center justify-between" style={{ minHeight: 32, borderBottom: `1px solid ${linka(isDark)}` }}>
                        <span className="text-[12.5px]" style={{ color: 'var(--text-secondary)' }}>{p}</span>
                        <span className="text-[12.5px] font-semibold tabular-nums" style={{ color: p === 'Po termínu' && h > 0 ? '#f87171' : 'var(--text-primary)' }}>{h}</span>
                    </div>
                ))}
                <p className="text-[10.5px] font-bold uppercase" style={{ color: muted(isDark), letterSpacing: '0.06em', margin: '18px 0 6px' }}>K dořešení</p>
                <KDoreseniSeznam polozky={kDoreseni} isDark={isDark} prazdne="Nic k dořešení." />
            </div>
        );
    };

    const zalozit = async () => {
        const token = document.querySelector('meta[name="csrf-token"]')?.content;
        setNove(n => ({ ...n, bezi: true, chyba: null }));
        const res = await fetch('/api/skoleni', {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-TOKEN': token },
            body: JSON.stringify({ nazev: nove.nazev, typ_zakonceni: nove.typ_zakonceni }),
        });
        if (res.ok) {
            const s = await res.json();
            setNove(null); setPohled('koncept');
            await nacti();
            setVybranyId(s.id);
        } else {
            setNove(n => ({ ...n, bezi: false, chyba: { typ: 'chyba', text: 'Nepodařilo se vytvořit' } }));
        }
    };

    return (
        <div className="flex flex-col" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <ModulLayout isMobile={isMobile} vybrano={!!vybranyId} mobilRezim mobilPanel={false} barva={SECTION_COLOR} isDark={isDark}
                seznam={seznam}
                detail={() => (
                    <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
                        <SkoleniEditor id={vybranyId} isDark={isDark} isMobile={isMobile}
                            onZavrit={() => { setVybranyId(null); setEdituje(false); }}
                            onZmena={nacti} onEditace={setEdituje}
                            rezervaDole={isMobile ? rezervaDole : null}
                            nadZalozkami={isMobile && kDoreseni.length > 0 ? <KDoreseniRadek polozky={kDoreseni} isDark={isDark} barva={SECTION_COLOR} /> : null} />
                    </div>
                )}
                panel={panel} />

            {nove && (
                <ModalShell onClose={() => setNove(null)} isDark={isDark} barva={SECTION_COLOR} ikona={GraduationCap} nadpis="Nové školení"
                    paticka={(
                        <div className="flex gap-2 justify-end">
                            <Tlacitko isDark={isDark} vyska={40} onClick={() => setNove(null)}>Zrušit</Tlacitko>
                            <Tlacitko isDark={isDark} vyska={40} primarni disabled={!nove.nazev.trim() || nove.bezi} onClick={zalozit}>
                                {nove.bezi ? 'Vytvářím…' : 'Vytvořit a upravit'}
                            </Tlacitko>
                        </div>
                    )}>
                    <div className="space-y-3" style={{ padding: 16 }}>
                        <Pole label="Název školení" isDark={isDark}>
                            <Vstup isDark={isDark} value={nove.nazev} autoFocus placeholder="Např. Školení HACCP"
                                onChange={e => setNove(n => ({ ...n, nazev: e.target.value }))}
                                onKeyDown={e => { if (e.key === 'Enter' && nove.nazev.trim()) zalozit(); }} />
                        </Pole>
                        <Pole label="Typ zakončení" isDark={isDark}>
                            <Vyber isDark={isDark} prazdna={false} value={nove.typ_zakonceni} onChange={v => setNove(n => ({ ...n, typ_zakonceni: v }))}
                                options={Object.entries(TYP_LABEL).map(([value, label]) => ({ value, label }))} />
                        </Pole>
                        <p className="text-[12px]" style={{ color: muted(isDark) }}>Školení vznikne jako koncept — obsah, kvíz a přiřazení doplníte v editoru.</p>
                        <Hlaska hlaska={nove.chyba} />
                    </div>
                </ModalShell>
            )}
        </div>
    );
}

function Splneni({ s, mutedColor, isDark }) {
    const celkem = s.prubehy_count || 0;
    const hotovo = s.dokonceno_count || 0;
    if (!celkem) return <span style={{ color: mutedColor }}>—</span>;
    return (
        <span className="flex items-center" style={{ gap: 8 }}>
            <span style={{ flex: 1, height: 4, borderRadius: 2, background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }}>
                <span style={{ display: 'block', height: '100%', borderRadius: 2, width: `${(hotovo / celkem) * 100}%`, background: SECTION_COLOR }} />
            </span>
            <span className="tabular-nums" style={{ fontSize: 12.5, color: 'var(--text-primary)', fontWeight: 600 }}>{hotovo}/{celkem}</span>
        </span>
    );
}

function RadekMrizka({ s, grid, aktivni, onClick, isDark, mutedColor, hranice }) {
    const Ikona = TYP_IKONA[s.typ_zakonceni] || BookOpen;
    return (
        <button type="button" onClick={onClick} className="w-full text-left"
            style={{
                ...grid, padding: '0 14px', minHeight: 50, border: 'none', borderBottom: `1px solid ${hranice}`,
                borderLeft: `3px solid ${aktivni ? SECTION_COLOR : 'transparent'}`,
                background: aktivni ? `${SECTION_COLOR}14` : 'transparent', cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)',
            }}>
            <span className="flex items-center min-w-0" style={{ gap: 10 }}>
                <span className="flex items-center justify-center" style={{ width: 30, height: 30, borderRadius: 7, background: `${SECTION_COLOR}1f`, color: SECTION_COLOR, flexShrink: 0 }}><Ikona size={15} /></span>
                <span className="truncate font-semibold" style={{ color: aktivni ? SECTION_COLOR : 'var(--text-primary)' }}>{s.nazev}</span>
            </span>
            <span className="flex items-center" style={{ gap: 6 }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: s.stav === 'aktivni' ? '#059669' : 'rgba(148,163,184,0.6)' }} />
                {STAV_LABEL[s.stav] || s.stav}
            </span>
            <span className="truncate">{TYP_LABEL[s.typ_zakonceni]}</span>
            <span className="tabular-nums" style={{ textAlign: 'right' }}>{s.sekce_count ?? '—'}</span>
            <span className="tabular-nums" style={{ textAlign: 'right' }}>{s.prubehy_count || <span style={{ color: mutedColor }}>—</span>}</span>
            <Splneni s={s} mutedColor={mutedColor} isDark={isDark} />
            <span className="tabular-nums" style={{ textAlign: 'right', color: s.po_terminu_count ? '#f87171' : mutedColor, fontWeight: s.po_terminu_count ? 700 : 500 }}>
                {s.po_terminu_count || '—'}
            </span>
            <span className="truncate" style={{ color: mutedColor }}>{datum(s.updated_at)}</span>
        </button>
    );
}

function RadekUzky({ s, aktivni, onClick, kompaktni, mutedColor, hranice }) {
    const Ikona = TYP_IKONA[s.typ_zakonceni] || BookOpen;
    const celkem = s.prubehy_count || 0;
    return (
        <button type="button" onClick={onClick} className="w-full text-left flex items-center"
            style={{
                gap: 12, padding: '0 12px', minHeight: kompaktni ? 60 : 54, border: 'none', borderBottom: `1px solid ${hranice}`,
                borderLeft: `3px solid ${aktivni ? SECTION_COLOR : 'transparent'}`,
                background: aktivni ? `${SECTION_COLOR}14` : 'transparent', cursor: 'pointer',
            }}>
            <span className="flex items-center justify-center" style={{ width: 32, height: 32, borderRadius: 8, background: `${SECTION_COLOR}1f`, color: SECTION_COLOR, flexShrink: 0 }}><Ikona size={16} /></span>
            <span className="min-w-0 flex-1">
                <span className="block truncate font-semibold" style={{ fontSize: kompaktni ? 15 : 13, color: aktivni ? SECTION_COLOR : 'var(--text-primary)' }}>{s.nazev}</span>
                <span className="block truncate" style={{ fontSize: kompaktni ? 12.5 : 11.5, color: mutedColor }}>
                    {STAV_LABEL[s.stav]} · {TYP_LABEL[s.typ_zakonceni]} · {s.sekce_count || 0} sekcí
                    {s.po_terminu_count ? <> · <span style={{ color: '#f87171', fontWeight: 600 }}>{s.po_terminu_count} po termínu</span></> : null}
                </span>
            </span>
            {celkem > 0 && <span className="tabular-nums" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}>{s.dokonceno_count || 0}/{celkem}</span>}
        </button>
    );
}
